import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import {
  TrendUp, Wallet, PiggyBank, ShieldCheck, ChartLine, Bell,
  FileText, Calculator, Globe, ChartPieSlice, Archive,
  Sparkle, Lightning, Star, ArrowRight, CheckCircle
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import ScreenshotCarousel from '../components/ScreenshotCarousel';
import Testimonials from '../components/Testimonials';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { login } = useContext(AuthContext) || {};
  const [demoLoading, setDemoLoading] = useState(false);
  const [launchInfo, setLaunchInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/paypal/config`)
      .then((r) => setLaunchInfo({ ...(r.data?.launch || {}), plans: r.data?.plans || null }))
      .catch(() => {});
  }, []);

  // « Essayer la démo » — connexion instantanée au compte démo partagé.
  const startDemo = async () => {
    setDemoLoading(true);
    try {
      const r = await axios.post(`${API}/auth/demo-login`);
      if (login) login(r.data.access_token);
      navigate('/dashboard');
    } catch {
      navigate('/auth');
    } finally {
      setDemoLoading(false);
    }
  };

  // ===== Nouveautés 2026 — fonctionnalités phares mises en avant =====
  // Toutes les chaînes (titre, description, badge "Nouveau") sont traduites
  // via `t()` — voir clés `landing.feat*` dans i18n/translations.js.
  const heroFeatures = [
    {
      icon: FileText,
      titleKey: 'landing.featAiImportTitle',
      descKey: 'landing.featAiImportDesc',
      image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80',
      hasBadge: true,
      testId: 'feat-ai-import',
    },
    {
      icon: Calculator,
      titleKey: 'landing.featBudgetExcelTitle',
      descKey: 'landing.featBudgetExcelDesc',
      image: 'https://images.unsplash.com/photo-1579621970795-87facc2f976d?auto=format&fit=crop&w=800&q=80',
      hasBadge: true,
      testId: 'feat-budget-excel',
    },
    {
      icon: Globe,
      titleKey: 'landing.featMultiCurrencyTitle',
      descKey: 'landing.featMultiCurrencyDesc',
      image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=800&q=80',
      hasBadge: true,
      testId: 'feat-multi-currency',
    },
    {
      icon: ChartPieSlice,
      titleKey: 'landing.featDashboardTitle',
      descKey: 'landing.featDashboardDesc',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
      hasBadge: false,
      testId: 'feat-dashboard',
    },
    {
      icon: ChartLine,
      titleKey: 'landing.featDividendsTitle',
      descKey: 'landing.featDividendsDesc',
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=800&q=80',
      hasBadge: false,
      testId: 'feat-dividends',
    },
    {
      icon: ChartPieSlice,
      titleKey: 'landing.featAnalysisTitle',
      descKey: 'landing.featAnalysisDesc',
      image: 'https://images.unsplash.com/photo-1543286386-713bdd548da4?auto=format&fit=crop&w=800&q=80',
      hasBadge: true,
      testId: 'feat-analysis',
    },
    {
      icon: Calculator,
      titleKey: 'landing.featToolsTitle',
      descKey: 'landing.featToolsDesc',
      image: 'https://images.unsplash.com/photo-1554224155-3a589877462e?auto=format&fit=crop&w=800&q=80',
      hasBadge: true,
      testId: 'feat-tools',
    },
    {
      icon: Archive,
      titleKey: 'landing.featReportsTitle',
      descKey: 'landing.featReportsDesc',
      image: 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?auto=format&fit=crop&w=800&q=80',
      hasBadge: false,
      testId: 'feat-reports',
    },
  ];

  // Mini-stats / preuves rapides — affichées en bandeau de réassurance
  const reassurance = [
    { icon: ShieldCheck, labelKey: 'landing.reassureEncryption', subKey: 'landing.reassureEncryptionSub' },
    { icon: Lightning, labelKey: 'landing.reassureRealtime', subKey: 'landing.reassureRealtimeSub' },
    { icon: Bell, labelKey: 'landing.reassureAlerts', subKey: 'landing.reassureAlertsSub' },
    { icon: Star, labelKey: 'landing.reassureFree', subKey: 'landing.reassureFreeSub' },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* ============ NAV ============ */}
      <nav className="border-b border-slate-300 bg-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <Wallet size={32} weight="duotone" className="text-blue-700 shrink-0" />
              <span className="text-xl sm:text-2xl font-bold tracking-tight text-black whitespace-nowrap">Auto-Gère</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <LanguageSelector variant="outline" />
              <Button
                data-testid="nav-login-btn"
                onClick={() => navigate('/auth')}
                className="hidden sm:inline-flex bg-white hover:bg-slate-50 text-black border-2 border-blue-600 hover:border-blue-700"
              >
                {t('nav.login')}
              </Button>
              <Button
                data-testid="nav-signup-btn"
                onClick={() => navigate('/auth?mode=register')}
                className="bg-blue-700 hover:bg-blue-800 text-white border-2 border-blue-700"
              >
                {t('nav.signup')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* ============ HERO ============ */}
      <section className="pt-16 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div
              data-testid="hero-badge-new"
              className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full bg-blue-50 border-2 border-blue-300 text-blue-800 text-sm font-bold"
            >
              <Sparkle size={16} weight="fill" className="text-blue-700" />
              {t('landing.heroBadge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-black mb-6 leading-tight">
              {t('landing.heroTitleMain')} <span className="text-blue-700">{t('landing.heroTitleAccent')}</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-700 mb-8 max-w-2xl mx-auto leading-relaxed">
              {t('landing.heroSubtitle')}
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button
                data-testid="hero-demo-btn"
                size="lg"
                onClick={startDemo}
                disabled={demoLoading}
                className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg gap-2 border-2 border-blue-700"
              >
                {demoLoading ? t('landing.demoLoading') : t('landing.tryDemo')}
              </Button>
              <Button
                data-testid="hero-get-started-btn"
                size="lg"
                onClick={() => navigate('/auth?mode=register')}
                className="bg-white hover:bg-blue-50 text-black border-2 border-blue-600 hover:border-blue-700 px-8 py-6 text-lg gap-2"
              >
                {t('landing.getStarted')}
                <ArrowRight size={20} weight="bold" />
              </Button>
            </div>
            <p className="mt-3 text-sm text-slate-600" data-testid="demo-subtext">
              {t('landing.demoSubtext')}
            </p>
            {/* Prix de lancement visible SANS défiler (conversion) */}
            {launchInfo?.active && launchInfo?.plans && (
              <div
                data-testid="launch-price-hero"
                className="mt-4 mx-auto max-w-2xl rounded-xl border-2 border-blue-600 bg-blue-50 px-5 py-3"
              >
                <p className="text-base sm:text-lg font-bold text-blue-900">
                  🚀 {t('landing.launchPriceHero')
                    .replace('{monthly}', String(launchInfo.plans.monthly.price))
                    .replace('{yearly}', String(launchInfo.plans.yearly.price))}
                </p>
                <p className="text-xs text-slate-700 mt-0.5">
                  {t('landing.launchPriceHeroSub')
                    .replace('{standard}', String(launchInfo.standard_monthly))}
                </p>
              </div>
            )}
            {/* Offre de lancement — compteur public seulement dès 50 membres */}
            {launchInfo?.active && launchInfo?.show_counter && launchInfo?.spots_left != null && (
              <div
                data-testid="launch-counter-banner"
                className="mt-5 mx-auto max-w-xl flex items-center justify-center gap-2 rounded-full border-2 border-amber-400 bg-amber-50 px-5 py-2.5"
              >
                <span className="text-lg">🚀</span>
                <p className="text-sm font-semibold text-amber-900">
                  {t('landing.launchCounter')
                    .replace('{count}', String(launchInfo.count ?? (launchInfo.max - launchInfo.spots_left)))
                    .replace('{left}', String(launchInfo.spots_left))}
                </p>
              </div>
            )}
            {/* Clarification légale : plateforme de SUIVI, non transactionnelle */}
            <div
              data-testid="tracking-only-notice"
              className="mt-6 mx-auto max-w-2xl flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-left"
            >
              <span className="text-lg leading-none shrink-0">ℹ️</span>
              <p className="text-sm text-slate-800">
                <strong>{t('landing.trackingOnlyTitle')}</strong> — {t('landing.trackingOnlyBody')}
              </p>
            </div>

            {/* Bandeau de réassurance compact */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
              {reassurance.map((r, i) => {
                const Icon = r.icon;
                return (
                  <div
                    key={r.labelKey}
                    data-testid={`reassure-${i}`}
                    className="bg-white border-2 border-blue-200 rounded-lg p-3 flex items-center gap-3 text-left"
                  >
                    <Icon size={28} weight="duotone" className="text-blue-700 shrink-0" />
                    <div>
                      <p className="font-bold text-black text-sm leading-tight">{t(r.labelKey)}</p>
                      <p className="text-xs text-slate-600 leading-tight">{t(r.subKey)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ DIAPORAMA DES ONGLETS ============ */}
      <ScreenshotCarousel />

      {/* Témoignages réels (masqué tant que vide) */}
      <Testimonials />

      {/* ============ NOUVEAUTÉS — 8 cards ============ */}
      <section className="py-20 px-4 bg-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-black mb-3">
              {t('landing.sectionTitle')}
            </h2>
            <p className="text-slate-700 max-w-2xl mx-auto">
              {t('landing.sectionSubtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {heroFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.testId}
                  data-testid={feature.testId}
                  className="bg-white border-2 border-blue-200 rounded-lg overflow-hidden hover:border-blue-600 hover:shadow-lg transition-all duration-200 flex flex-col"
                >
                  {feature.image && (
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={feature.image}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent" />
                      {feature.hasBadge && (
                        <span className="absolute top-3 right-3 text-[11px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-blue-700 text-white shadow">
                          {t('landing.badgeNew')}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-3 inline-flex w-fit mb-4">
                      <Icon size={28} weight="duotone" className="text-blue-700" />
                    </div>
                    <h3 className="text-xl font-bold text-black mb-2 leading-snug">{t(feature.titleKey)}</h3>
                    <p className="text-slate-700 leading-relaxed text-sm">{t(feature.descKey)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ CTA INTERMÉDIAIRE — rappel d'action à mi-page ============ */}
      <section className="py-12 px-4 bg-slate-200 border-t border-slate-300" data-testid="midpage-cta">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-lg font-bold text-black mb-4">{t('landing.midCtaTitle')}</p>
          <Button
            data-testid="midpage-signup-btn"
            size="lg"
            onClick={() => navigate('/auth?mode=register')}
            className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg gap-2 border-2 border-blue-700"
          >
            {t('landing.getStarted')}
            <ArrowRight size={20} weight="bold" />
          </Button>
          <p className="text-xs text-slate-600 mt-3">{t('landing.ctaTrust')}</p>
        </div>
      </section>

      {/* ============ DÉTAIL VISUEL — FEATURE PHARE : PORTEFEUILLE MULTI-DEVISES ============ */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto space-y-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-blue-50 border-2 border-blue-300 text-blue-800 text-xs font-bold uppercase tracking-wide mb-4">
                {t('landing.portfolioBadge')}
              </span>
              <h2 className="text-3xl font-bold text-black mb-4 leading-tight">
                {t('landing.portfolioTitlePart1')} <span className="text-blue-700">{t('landing.portfolioTitleAccent')}</span>{t('landing.portfolioTitlePart2')}
              </h2>
              <ul className="space-y-3 text-slate-800">
                <li className="flex gap-3">
                  <Globe size={22} weight="duotone" className="text-blue-700 shrink-0 mt-0.5" />
                  <span><strong>{t('landing.portfolioBullet1Strong')}</strong>{t('landing.portfolioBullet1Rest')}</span>
                </li>
                <li className="flex gap-3">
                  <Globe size={22} weight="duotone" className="text-blue-700 shrink-0 mt-0.5" />
                  <span><strong>{t('landing.portfolioBullet2Strong')}</strong>{t('landing.portfolioBullet2Rest')}</span>
                </li>
                <li className="flex gap-3">
                  <TrendUp size={22} weight="duotone" className="text-blue-700 shrink-0 mt-0.5" />
                  <span><strong>{t('landing.portfolioBullet3Strong')}</strong>{t('landing.portfolioBullet3Rest')}</span>
                </li>
                <li className="flex gap-3">
                  <PiggyBank size={22} weight="duotone" className="text-blue-700 shrink-0 mt-0.5" />
                  <span><strong>{t('landing.portfolioBullet4Strong')}</strong>{t('landing.portfolioBullet4Rest')}</span>
                </li>
              </ul>
            </div>
            <div className="bg-blue-50 border-2 border-blue-300 rounded-2xl p-6 shadow-lg">
              <div className="bg-white border-2 border-blue-200 rounded-lg p-4 space-y-2">
                {[
                  { flag: '\u{1F1FA}\u{1F1F8}', sym: 'AGNC', native: 'USD', price: '10.23', total: '15,14 $' },
                  { flag: '\u{1F1E8}\u{1F1E6}', sym: 'JEPI.TO', native: 'CAD', price: '24.45', total: '24,45 $' },
                  { flag: '\u{1F1EB}\u{1F1F7}', sym: 'AIR.PA', native: 'EUR', price: '178.60', total: '264,33 $' },
                  { flag: '\u{1F1EE}\u{1F1F3}', sym: 'RELIANCE.NS', native: 'INR', price: '2 845.50', total: '46,17 $' },
                ].map((r) => (
                  <div key={r.sym} className="flex items-center gap-2 text-sm border-b border-blue-100 last:border-0 pb-2 last:pb-0">
                    <span className="text-2xl leading-none">{r.flag}</span>
                    <span className="font-bold text-black w-28">{r.sym}</span>
                    <span className="ml-auto font-mono text-black tabular-nums">{r.price}</span>
                    <span className="text-[10px] font-bold text-slate-500 uppercase w-10">{r.native}</span>
                    <span className="font-bold text-blue-700 tabular-nums w-24 text-right">{r.total}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-700 mt-3 text-center italic">
                {t('landing.portfolioCaption')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TARIFS ============ */}
      <section className="py-20 px-4 bg-white border-t border-slate-200" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 mb-4 rounded-full bg-blue-50 border-2 border-blue-300 text-blue-800 text-xs font-bold uppercase tracking-wide">
              {t('landing.pricingBadge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-black mb-3">{t('landing.pricingTitle')}</h2>
            <p className="text-base md:text-lg text-slate-700 max-w-2xl mx-auto">{t('landing.pricingSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {/* Gratuit */}
            <div data-testid="pricing-free-card" className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-6 flex flex-col">
              <h3 className="text-lg font-bold text-black">{t('landing.pricingFreeTitle')}</h3>
              <p className="mt-2"><span className="text-4xl font-bold text-black">0 $</span></p>
              <p className="text-sm text-slate-600 mb-5">{t('landing.pricingFreeSub')}</p>
              <ul className="space-y-2.5 text-sm text-slate-800 flex-1">
                {[t('landing.pricingFree1'), t('landing.pricingFree2'), t('landing.pricingFree3')].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle size={18} weight="fill" className="text-blue-600 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid="pricing-free-btn"
                onClick={() => navigate('/auth?mode=register')}
                className="mt-6 w-full bg-white hover:bg-slate-100 text-black border-2 border-slate-400"
              >
                {t('landing.pricingFreeCta')}
              </Button>
            </div>
            {/* Premium mensuel — offre de lancement */}
            <div data-testid="pricing-monthly-card" className="relative rounded-2xl border-2 border-blue-600 bg-blue-50 p-6 flex flex-col shadow-lg">
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-blue-700 text-white text-xs font-bold whitespace-nowrap">
                🚀 {t('landing.pricingLaunchBadge')}
              </span>
              <h3 className="text-lg font-bold text-black mt-1">{t('landing.pricingMonthlyTitle')}</h3>
              <p className="mt-2 flex items-baseline gap-2 flex-wrap">
                <span className="text-4xl font-bold text-blue-800">{launchInfo?.plans?.monthly?.price ?? 4.99} $</span>
                <span className="text-sm text-slate-700">{t('landing.pricingPerMonth')}</span>
                {launchInfo?.standard_monthly && (
                  <span className="text-base text-slate-500 line-through">{launchInfo.standard_monthly} $</span>
                )}
              </p>
              <p className="text-sm text-blue-900 font-semibold mb-5">{t('landing.pricingFrozen')}</p>
              <ul className="space-y-2.5 text-sm text-slate-800 flex-1">
                {[t('landing.pricingPrem1'), t('landing.pricingPrem2'), t('landing.pricingPrem3'), t('landing.pricingPrem4')].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle size={18} weight="fill" className="text-blue-700 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid="pricing-monthly-btn"
                onClick={() => navigate('/auth?mode=register')}
                className="mt-6 w-full bg-blue-700 hover:bg-blue-800 text-white border-2 border-blue-700 font-bold"
              >
                {t('landing.pricingPremiumCta')}
              </Button>
            </div>
            {/* Premium annuel */}
            <div data-testid="pricing-yearly-card" className="rounded-2xl border-2 border-blue-300 bg-white p-6 flex flex-col">
              <h3 className="text-lg font-bold text-black">{t('landing.pricingYearlyTitle')}</h3>
              <p className="mt-2 flex items-baseline gap-2 flex-wrap">
                <span className="text-4xl font-bold text-blue-800">{launchInfo?.plans?.yearly?.price ?? 39.99} $</span>
                <span className="text-sm text-slate-700">{t('landing.pricingPerYear')}</span>
                {launchInfo?.standard_yearly && (
                  <span className="text-base text-slate-500 line-through">{launchInfo.standard_yearly} $</span>
                )}
              </p>
              <p className="text-sm text-blue-900 font-semibold mb-5">{t('landing.pricingYearlySave')}</p>
              <ul className="space-y-2.5 text-sm text-slate-800 flex-1">
                {[t('landing.pricingPrem1'), t('landing.pricingPrem2'), t('landing.pricingPrem3'), t('landing.pricingPrem4')].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle size={18} weight="fill" className="text-blue-600 shrink-0 mt-0.5" />{f}
                  </li>
                ))}
              </ul>
              <Button
                data-testid="pricing-yearly-btn"
                onClick={() => navigate('/auth?mode=register')}
                className="mt-6 w-full bg-white hover:bg-blue-50 text-blue-800 border-2 border-blue-600 font-bold"
              >
                {t('landing.pricingPremiumCta')}
              </Button>
            </div>
          </div>
          <p className="text-center text-xs text-slate-600 mt-6" data-testid="pricing-note">{t('landing.pricingNote')}</p>
        </div>
      </section>

      {/* ============ CTA FINAL ============ */}
      <section className="py-20 px-4 bg-blue-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            {t('landing.ctaTitleNew')}
          </h2>
          <p className="text-lg text-blue-50 mb-8 max-w-2xl mx-auto">
            {t('landing.ctaSubtitleNew')}
          </p>
          <Button
            data-testid="cta-get-started-btn"
            size="lg"
            onClick={() => navigate('/auth?mode=register')}
            className="bg-white hover:bg-slate-50 text-blue-700 border-2 border-white px-8 py-6 text-lg font-bold gap-2"
          >
            {t('landing.ctaButtonNew')}
            <ArrowRight size={20} weight="bold" />
          </Button>
          <p className="text-xs text-blue-100 mt-4">{t('landing.ctaTrust')}</p>
        </div>
      </section>

      <footer className="border-t border-slate-300 py-8 px-4 bg-slate-200">
        <div className="max-w-7xl mx-auto text-center text-black text-sm space-y-3">
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs font-semibold" data-testid="footer-links">
            <Link to="/faq" className="hover:text-blue-700 transition-colors" data-testid="footer-faq-link">{t('landing.footerFaq')}</Link>
            <span className="text-slate-400">·</span>
            <Link to="/refund-policy" className="hover:text-blue-700 transition-colors" data-testid="footer-refund-link">{t('landing.footerRefund')}</Link>
            <span className="text-slate-400">·</span>
            <Link to="/privacy-policy" className="hover:text-blue-700 transition-colors" data-testid="footer-privacy-link">{t('landing.footerPrivacy')}</Link>
            <span className="text-slate-400">·</span>
            <a href="mailto:support@autogere.ca" className="hover:text-blue-700 transition-colors" data-testid="footer-contact-link">{t('landing.footerContact')}</a>
          </nav>
          <p>{t('landing.footer')}</p>
          {/* Badge Shipit — backlink réciproque pour SEO + crédibilité "vu sur".
              Thème light choisi pour harmonie avec le footer clair (bg-slate-200). */}
          <div className="pt-2 flex justify-center" data-testid="footer-badges">
            <a
              href="https://www.shipit.buzz/products/auto-gre?ref=badge"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shipit-badge-link"
              aria-label="Featured on Shipit"
              className="inline-block transition-transform hover:scale-105"
            >
              <img
                src="https://www.shipit.buzz/api/products/auto-gre/badge?theme=light"
                alt="Featured on Shipit"
                loading="lazy"
                width="180"
                height="54"
                className="h-12 w-auto"
              />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
